import { Check, Flame } from "lucide-react";
import { toast } from "sonner";
import { useCrm } from "./CrmContext";
import { api, errorMessage } from "@/lib/api";
import { TASK_KINDS, initials, isOverdue, isToday, stageOf, when } from "@/lib/crm";

export const Avatar = ({ name, size = 34, tone = "#3A4553" }) => (
  <span className="grid shrink-0 place-items-center rounded-full font-mono font-medium text-white" style={{ width: size, height: size, fontSize: size * 0.34, background: tone }}>{initials(name)}</span>
);

export const StagePill = ({ stage }) => {
  const s = stageOf(stage);
  return <span className="crm-pill" style={{ color: s.color, background: `${s.color}14`, borderColor: `${s.color}33` }} data-testid={`stage-pill-${stage}`}>{s.label}</span>;
};

export const HotFlag = ({ rating }) => rating === "hot" ? <Flame className="h-3.5 w-3.5 text-[#E11D48]" aria-label="Hot lead" /> : rating === "warm" ? <Flame className="h-3.5 w-3.5 text-[#F59E0B]" aria-label="Warm lead" /> : null;

export const Kpi = ({ label, value, hint, tone = "#0F172A", testId, onClick }) => (
  <button type="button" onClick={onClick} className="crm-card group p-4 text-left transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-md" data-testid={testId}>
    <p className="crm-label">{label}</p>
    <p className="mt-2 font-mono text-[28px] font-semibold leading-none tracking-tight" style={{ color: tone }}>{value}</p>
    {hint && <p className="mt-2 text-[12px] text-[#64748B]">{hint}</p>}
  </button>
);

export const Panel = ({ title, action, children, testId, className = "" }) => (
  <section className={`crm-card flex min-h-0 flex-col ${className}`} data-testid={testId}>
    <div className="flex items-center justify-between border-b border-[#EEF2F7] px-4 py-3"><h2 className="text-[14px] font-semibold tracking-tight">{title}</h2>{action}</div>
    <div className="min-h-0 flex-1">{children}</div>
  </section>
);

export const Empty = ({ children, testId }) => <p className="px-4 py-8 text-center text-[13px] text-[#94A3B8]" data-testid={testId}>{children}</p>;

export const TaskRow = ({ task, showLead = true }) => {
  const { upsertTask, openPerson } = useCrm();
  const [label, Icon] = TASK_KINDS[task.kind] || TASK_KINDS.follow_up;
  const done = task.status === "done";
  const late = !done && isOverdue(task.due_at);
  const toggle = async () => {
    try { const { data } = await api.patch(`/admin/tasks/${task.id}`, { status: done ? "open" : "done" }); upsertTask(data); if (!done) toast.success("Task completed"); } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="group flex items-center gap-3 border-b border-[#F1F5F9] px-4 py-2.5 last:border-0 hover:bg-[#F8FAFC]" data-testid={`task-row-${task.id}`}>
      <button type="button" onClick={toggle} aria-label={done ? "Reopen task" : "Complete task"} data-testid={`task-complete-${task.id}`}
        className={`grid h-5 w-5 shrink-0 place-items-center rounded border transition-colors ${done ? "border-[#16A34A] bg-[#16A34A] text-white" : "border-[#CBD5E1] hover:border-[#16A34A]"}`}>{done && <Check className="h-3.5 w-3.5" />}</button>
      <Icon className="h-4 w-4 shrink-0 text-[#64748B]" aria-label={label} />
      <div className="min-w-0 flex-1">
        <p className={`truncate text-[13px] ${done ? "text-[#94A3B8] line-through" : "font-medium"}`}>{task.title}</p>
        <p className="truncate text-[11.5px] text-[#64748B]">
          {showLead && task.lead_id && <button type="button" onClick={() => openPerson(task.lead_id)} className="mr-2 font-medium text-[#2B8FD6] hover:underline" data-testid={`task-lead-link-${task.id}`}>{task.lead_name || "Open person"}</button>}
          {task.assigned_agent_name}
        </p>
      </div>
      <span className={`shrink-0 font-mono text-[11px] ${late ? "font-semibold text-[#E11D48]" : isToday(task.due_at) ? "text-[#EA580C]" : "text-[#94A3B8]"}`} data-testid={`task-due-${task.id}`}>{late ? "Overdue · " : ""}{when(task.due_at)}</span>
    </div>
  );
};
